/**
 * KPI Card Component
 *
 * Hero stat card for executive dashboard.
 * Displays key metrics with optional trend indicators.
 */

import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';

// Format value based on type
function formatValue(value, format) {
  if (value === null || value === undefined) return 'N/A';

  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    case 'percent':
      // If value is between 0 and 1, multiply by 100
      const pct = value <= 1 && value >= 0 ? value * 100 : value;
      return `${pct.toFixed(1)}%`;
    case 'decimal':
      return value.toFixed(1);
    default:
      return typeof value === 'number'
        ? new Intl.NumberFormat('en-US').format(Math.round(value))
        : value;
  }
}

export default function KPICard({
  title,
  value,
  change,
  format = 'number',
  icon: Icon,
  subtitle,
  loading = false,
}) {
  const formattedValue = formatValue(value, format);

  // Change is in decimal form (0.15 = 15%)
  const changePercent = change !== undefined ? Math.abs(change * 100).toFixed(1) : null;
  const isPositive = change >= 0;
  const changeColor = isPositive ? 'text-green-600' : 'text-red-600';
  const ChangeTrendIcon = isPositive ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
        <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-500 uppercase tracking-wide font-medium">
            {title}
          </p>
          <p className="text-3xl font-bold mt-2 text-gray-900">
            {formattedValue}
          </p>
          {changePercent !== null && (
            <div className={`flex items-center gap-1 mt-2 ${changeColor}`}>
              <ChangeTrendIcon className="w-4 h-4" />
              <span className="text-sm font-medium">
                {changePercent}% vs last period
              </span>
            </div>
          )}
          {subtitle && (
            <p className="text-sm text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>
        {Icon && (
          <div className="p-3 bg-blue-50 rounded-lg">
            <Icon className="w-6 h-6 text-blue-600" />
          </div>
        )}
      </div>
    </div>
  );
}
